import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Notification } from '@/types/notification';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  lastPollTimestamp: string | null;
  isPolling: boolean;
  bellAnimating: boolean;
  shootingStarOrigin: { x: number; y: number } | null;
}

interface NotificationActions {
  setNotifications: (notifications: Notification[]) => void;
  addNotifications: (notifications: Notification[]) => void;
  processIncoming: (notifications: Notification[]) => Notification[];
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  setUnreadCount: (count: number) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  setLastPollTimestamp: (timestamp: string | null) => void;
  setIsPolling: (isPolling: boolean) => void;
  setBellAnimating: (animating: boolean) => void;
  triggerShootingStar: (from: { x: number; y: number }) => void;
  clearShootingStar: () => void;
  reset: () => void;
}

interface NotificationStore extends NotificationState, NotificationActions {}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  lastPollTimestamp: null,
  isPolling: false,
  bellAnimating: false,
  shootingStarOrigin: null,
};

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set) => ({
      ...initialState,

      setNotifications: (notifications) =>
        set({ notifications }),

      addNotifications: (newNotifications) =>
        set((state) => {
          const existingIds = new Set(state.notifications.map((n) => n.id));
          const uniqueNew = newNotifications.filter((n) => !existingIds.has(n.id));
          return {
            notifications: [...uniqueNew, ...state.notifications].slice(0, 100),
          };
        }),

      processIncoming: (incoming) => {
        const toastWorthy: Notification[] = [];

        set((state) => {
          const existingMap = new Map(state.notifications.map((n) => [n.id, n]));
          const updatedNotifications = [...state.notifications];

          for (const notification of incoming) {
            const existing = existingMap.get(notification.id);

            if (!existing) {
              updatedNotifications.unshift(notification);
              toastWorthy.push(notification);
            } else if (
              notification.updated_at &&
              notification.updated_at !== existing.updated_at
            ) {
              const idx = updatedNotifications.findIndex((n) => n.id === notification.id);
              if (idx !== -1) {
                updatedNotifications[idx] = notification;
              }
              toastWorthy.push(notification);
            }
          }

          if (toastWorthy.length === 0) return state;

          return {
            notifications: updatedNotifications.slice(0, 100),
            bellAnimating: true,
          };
        });

        return toastWorthy;
      },

      updateNotification: (id, updates) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, ...updates } : n
          ),
        })),

      setUnreadCount: (count) =>
        set({ unreadCount: count }),

      markAsRead: (notificationId) =>
        set((state) => {
          const target = state.notifications.find((n) => n.id === notificationId);
          const wasUnread = target && !target.is_read;
          return {
            notifications: state.notifications.map((n) =>
              n.id === notificationId
                ? { ...n, is_read: true, read_at: new Date().toISOString() }
                : n
            ),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        }),

      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({
            ...n,
            is_read: true,
            read_at: new Date().toISOString(),
          })),
          unreadCount: 0,
        })),

      setLastPollTimestamp: (timestamp) =>
        set({ lastPollTimestamp: timestamp }),

      setIsPolling: (isPolling) =>
        set({ isPolling }),

      setBellAnimating: (animating) =>
        set({ bellAnimating: animating }),

      triggerShootingStar: (from) =>
        set({ shootingStarOrigin: from }),

      clearShootingStar: () =>
        set({ shootingStarOrigin: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'notification-storage',
      version: 1,
      partialize: (state) => ({
        lastPollTimestamp: state.lastPollTimestamp,
      }),
    }
  )
);
